import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import {
  Menu,
  menuBar,
  MenuBarButton,
  MenuBarDropdown,
  MenuBarDropdownContentDirective,
  MenuBarPillSwitch,
  MenuBarService,
  MenuItem,
} from '@softapps/core';
import {
  CalendarViewType,
  RosterHostComponent,
  RosterService,
} from '@softapps/pms/roster-core';
import { handleRequestError, title } from '@softline/application';
import {
  DsContextMenuComponent,
  DsContextMenuItemComponent,
  MessageBarService,
  translator,
  ButtonComponent,
  IconComponent,
  SoftSlotDirective,
  modalFn,
} from '@softline/ui-core';
import { TabItem } from '@softline/ui-core/tabs';
import { DeliveryNoteRosterService } from '../../services/delivery-note-roster.service';
import { RosterDeliveryContent } from '../../types/roster-delivery-content';
import { DeliveryNotePlanMode } from '../../types/delivery-note-plan-type';
import { ExportDialogComponent } from '../../dialogs/export-dialog/export-dialog.component';

@Component({
  selector: 'soft-dn-roster-menu-bar',
  imports: [
    Menu,
    MenuBarButton,
    MenuBarDropdown,
    MenuBarDropdownContentDirective,
    MenuBarPillSwitch,
    MenuItem,
    DsContextMenuComponent,
    DsContextMenuItemComponent,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
  ],
  templateUrl: './dn-roster-menu-bar.html',
  styles: `
    :host {
      display: contents;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DnRosterMenuBar {
  readonly t = translator();
  protected readonly hostComponent = inject(RosterHostComponent);
  readonly deliveryNoteRosterService = inject(RosterService) as DeliveryNoteRosterService;
  private readonly messageBar = inject(MessageBarService);
  private readonly injector = inject(Injector);
  private readonly menuBarService = inject(MenuBarService);
  private readonly modal = modalFn();

  private readonly viewTemplate = viewChild.required('viewMenuBar', {
    read: TemplateRef,
  });
  private readonly buttonTemplate = viewChild.required('buttons', {
    read: TemplateRef,
  });
  private readonly selectionLeadingTemplate = viewChild.required(
    'selectionLeadingMenuBar',
    { read: TemplateRef }
  );
  private readonly selectionTrailingTemplate = viewChild.required(
    'selectionTrailingMenuBar',
    { read: TemplateRef }
  );
  private readonly contextMenuTemplate = viewChild.required('contextMenu', {
    read: TemplateRef,
  });

  readonly viewTypeTabs = computed<TabItem<CalendarViewType>[]>(() => [
    { value: 'day', title: this.t('#DN_ROSTER.VIEW_TYPE_DAY')() as string },
    { value: 'workWeek', title: this.t('#DN_ROSTER.VIEW_TYPE_WORK_WEEK')() as string },
    { value: 'week', title: this.t('#DN_ROSTER.VIEW_TYPE_WEEK')() as string },
    { value: 'month', title: this.t('#DN_ROSTER.VIEW_TYPE_MONTH')() as string },
  ]);

  readonly viewTypeLabel = computed(
    () =>
      this.viewTypeTabs().find((o) => o.value === this.hostComponent.calendarViewType())
        ?.title ?? ''
  );

  readonly detailPillOptions = computed(() => [
    {
      label: this.t('#DN_ROSTER.DETAILS_COMPACT')() as string,
      value: 'compact',
      icon: 'fa-regular fa-compress',
    },
    {
      label: this.t('#DN_ROSTER.DETAILS_ALL')() as string,
      value: 'all',
      icon: 'fa-regular fa-table-list',
    },
  ]);

  readonly mailableSelectedCount = computed(
    () =>
      (
        this.deliveryNoteRosterService.selectedEvents() as unknown as RosterDeliveryContent[]
      ).filter((e) => e.additionalData?.exported).length
  );

  readonly isMailButtonDisabled = computed(() => this.mailableSelectedCount() === 0);

  readonly mailCount = computed(() =>
    this.mailableSelectedCount() > 0 ? '(' + this.mailableSelectedCount() + ')' : ''
  );

  readonly selectedCount = computed(
    () => this.deliveryNoteRosterService.selectedEvents().length
  );

  readonly deletableSelectedCount = computed(
    () =>
      (
        this.deliveryNoteRosterService.selectedEvents() as unknown as RosterDeliveryContent[]
      ).filter((e) => {
        const ad = e.additionalData;
        return !(
          (ad?.exported && ad?.mode === DeliveryNotePlanMode.Detail) ||
          ad?.deleted
        );
      }).length
  );

  readonly isDeleteButtonDisabled = computed(() => this.deletableSelectedCount() === 0);

  constructor() {
    title(this.t('#DN_ROSTER.TITLE'));

    menuBar({
      key: 'pms/roster/view',
      title: this.t('#DN_ROSTER.VIEW_TAB_TITLE'),
      template: this.viewTemplate,
      align: 'leading',
      separated: true,
      order: 0,
    });
    menuBar({
      key: 'pms/roster/view',
      title: this.t('#DN_ROSTER.VIEW_TAB_TITLE'),
      template: this.buttonTemplate,
      align: 'trailing',
      separated: true,
      order: 1,
    });

    menuBar({
      key: 'wws/delivery-note-roster/selection',
      title: this.t('#DN_ROSTER.SELECTION_TAB_TITLE'),
      template: this.selectionLeadingTemplate,
      align: 'leading',
      separated: true,
      visible: computed(() => this.selectedCount() > 0),
      order: 0,
    });
    menuBar({
      key: 'wws/delivery-note-roster/selection',
      title: this.t('#DN_ROSTER.SELECTION_TAB_TITLE'),
      template: this.selectionTrailingTemplate,
      align: 'trailing',
      separated: true,
      visible: computed(() => this.selectedCount() > 0),
      order: 1,
    });

    // Own the DN context menu here so the shared calendar opens it via
    // resolveEventMenu/resolveCalendarMenu.
    effect(() => {
      this.deliveryNoteRosterService.contextMenu.set(this.contextMenuTemplate());
    });

    // Swap the menu bar into "selection mode" while events are selected,
    // and back to the normal view once the selection empties.
    effect(() => {
      if (this.selectedCount() > 0) {
        untracked(() =>
          this.menuBarService.activateKey('wws/delivery-note-roster/selection')
        );
      } else {
        untracked(() => this.menuBarService.activateIndex(0));
      }
    });
  }

  setDetailOption(value: string): void {
    this.hostComponent.calendarDetails.set(value as 'compact' | 'all');
  }

  async import(): Promise<void> {
    try {
      await this.deliveryNoteRosterService.importData();
      this.messageBar.success('#DN_ROSTER.MESSAGES.IMPORT_SUCCESS');
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async export(): Promise<void> {
    const result = await this.modal({
      component: ExportDialogComponent,
      title: this.t('#DN_ROSTER.DIALOG.EXPORT_TITLE')(),
    });
    if (!result || result === 'DISMISSED' || result === 'CANCEL') return;

    try {
      await this.deliveryNoteRosterService.exportData(result.beginDate, result.endDate);
      this.messageBar.success('#DN_ROSTER.MESSAGES.EXPORT_SUCCESS');
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async deleteSelected(): Promise<void> {
    try {
      await this.deliveryNoteRosterService.deleteSelectedEvents();
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async sendMail(): Promise<void> {
    const { succeeded, failures } = await this.deliveryNoteRosterService.sendMail();
    if (failures.length === 0) {
      this.messageBar.success('#DN_ROSTER.MESSAGES.MAIL_SUCCESS');
    } else {
      if (succeeded > 0) {
        this.messageBar.warning(
          `${succeeded} ${this.t('#DN_ROSTER.MESSAGES.MAIL_SUCCESS_COUNT')()}, ${failures.length} ${this.t('#DN_ROSTER.MESSAGES.MAIL_FAILED_COUNT')()}`
        );
      }
      for (const { error } of failures) {
        handleRequestError(error, this.injector, { show: true });
      }
    }
  }
}
